import React, { FC, useState, useEffect, useContext, useRef } from 'react';
import { ProfileSection } from './ProfileSection';
import { sessionStore, SocialLink } from 'src/store/sessionStore';
import { useUpdateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { ProfileEditContext } from '../ProfileEditContext';
import { Modal } from 'antd';
import { toast } from 'src/utils/appAlert';
import './SocialLinksSection.scss';
import { ReactComponent as ProfileRemove2Icon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove2.svg';
import { ReactComponent as ProfileRemoveIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/profile-remove.svg';
import { ReactComponent as CloseIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/close.svg';

interface PresetPlatform {
  name: string;
}

const PRESET_PLATFORMS: PresetPlatform[] = [
  { name: 'Facebook' },
  { name: 'Instagram' },
  { name: 'Twitter' },
  { name: 'YouTube' },
  { name: 'LinkedIn' },
  { name: 'TikTok' },
  { name: 'Website' },
];

export const getPlatformDomainError = (platform: string, url: string): string | null => {
  const normPlatform = platform.trim().toLowerCase();
  const normUrl = url.trim().toLowerCase();

  if (normPlatform.includes('facebook') || normPlatform === 'fb') {
    if (
      !normUrl.includes('facebook') &&
      !normUrl.includes('fb.com') &&
      !normUrl.includes('fb.watch')
    ) {
      return 'URL must contain facebook (e.g. https://facebook.com/...)';
    }
  } else if (normPlatform.includes('instagram') || normPlatform.includes('insta')) {
    if (!normUrl.includes('instagram') && !normUrl.includes('instagr.am')) {
      return 'URL must contain instagram (e.g. https://instagram.com/...)';
    }
  } else if (
    normPlatform.includes('twitter') ||
    normPlatform === 'x' ||
    normPlatform.includes('twitter / x')
  ) {
    if (!normUrl.includes('twitter') && !normUrl.includes('x.com') && !normUrl.includes('t.co')) {
      return 'URL must contain twitter or x.com (e.g. https://twitter.com/...)';
    }
  } else if (normPlatform.includes('youtube') || normPlatform === 'yt') {
    if (!normUrl.includes('youtube') && !normUrl.includes('youtu.be')) {
      return 'URL must contain youtube (e.g. https://youtube.com/...)';
    }
  } else if (normPlatform.includes('linkedin')) {
    if (!normUrl.includes('linkedin')) {
      return 'URL must contain linkedin (e.g. https://linkedin.com/...)';
    }
  } else if (normPlatform.includes('tiktok')) {
    if (!normUrl.includes('tiktok')) {
      return 'URL must contain tiktok (e.g. https://tiktok.com/...)';
    }
  }

  return null;
};

export const getSocialIcon = (platform: string) => {
  const normalized = platform.trim().toLowerCase();
  if (normalized.includes('facebook') || normalized.includes('fb')) {
    return (
      <svg className="social-platform-svg facebook" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }
  if (normalized.includes('instagram') || normalized.includes('insta')) {
    return (
      <svg className="social-platform-svg instagram" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    );
  }
  if (normalized.includes('twitter') || normalized.includes(' x') || normalized === 'x') {
    return (
      <svg className="social-platform-svg twitter" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    );
  }
  if (normalized.includes('youtube') || normalized.includes('yt')) {
    return (
      <svg className="social-platform-svg youtube" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    );
  }
  if (normalized.includes('linkedin')) {
    return (
      <svg className="social-platform-svg linkedin" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    );
  }
  if (normalized.includes('tiktok')) {
    return (
      <svg className="social-platform-svg tiktok" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-1.01-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.84 3.48 2.72 1.02-.04 1.98-.59 2.51-1.46.43-.64.6-1.41.6-2.18V.02z" />
      </svg>
    );
  }

  // Fallback: Globe / Link icon
  return (
    <svg
      className="social-platform-svg website"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
};

export const SocialLinksSection: FC = () => {
  const { currentUser } = sessionStore();
  const [isEditing, setIsEditing] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { mutateAsync: updateProfileBackend, isPending } = useUpdateTrainerProfile();
  const { globalEditAction, registerEditState } = useContext(ProfileEditContext);

  const [links, setLinks] = useState<SocialLink[]>(
    currentUser?.socialLinks?.length ? currentUser.socialLinks : [],
  );
  const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState<number | null>(null);
  const [linkToRemove, setLinkToRemove] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Sync with global bulk edit
  useEffect(() => {
    if (globalEditAction.count > 0) {
      if (globalEditAction.type === 'OPEN') {
        setIsEditing(true);
        setHasSubmitted(false);
      } else if (globalEditAction.type === 'CLOSE') {
        setIsEditing(false);
      }
    }
  }, [globalEditAction]);

  // Register section with edit context
  useEffect(() => {
    registerEditState('socialLinks', isEditing);
  }, [isEditing, registerEditState]);

  // Sync state when currentUser changes and not editing
  useEffect(() => {
    if (currentUser && !isEditing) {
      setLinks(currentUser.socialLinks?.length ? currentUser.socialLinks : []);
    }
  }, [currentUser, isEditing]);

  // Close autocomplete on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveAutocompleteIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddLink = () => {
    if (links.length >= 10) {
      toast.error('You can add up to 10 social media links.');
      return;
    }
    setLinks([...links, { platform: '', url: '' }]);
    setHasSubmitted(false);
  };

  const handleRemoveLinkClick = (index: number) => {
    const item = links[index];
    if (!item.platform.trim() && !item.url.trim()) {
      setLinks(links.filter((_, i) => i !== index));
    } else {
      setLinkToRemove(index);
    }
  };

  const confirmRemoveLink = () => {
    if (linkToRemove !== null) {
      setLinks(links.filter((_, i) => i !== linkToRemove));
      setLinkToRemove(null);
    }
  };

  const handleUpdateField = (index: number, field: 'platform' | 'url', value: string) => {
    setLinks(
      links.map((link, i) => {
        if (i === index) {
          return { ...link, [field]: value };
        }
        return link;
      }),
    );
  };

  const handlePlatformBlur = (index: number, rawVal: string) => {
    const trimmed = rawVal.trim();
    handleUpdateField(index, 'platform', trimmed);
  };

  const handleUrlBlur = (index: number, rawVal: string) => {
    let trimmed = rawVal.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }
    handleUpdateField(index, 'url', trimmed);
  };

  const handleSelectAutocomplete = (index: number, platform: PresetPlatform) => {
    setLinks(
      links.map((link, i) => {
        if (i === index) {
          return { ...link, platform: platform.name };
        }
        return link;
      }),
    );
    setActiveAutocompleteIndex(null);
  };

  // Validation function
  const validateLinks = () => {
    const rowErrors: { platform?: string; url?: string }[] = [];
    let hasError = false;

    links.forEach((link) => {
      const err: { platform?: string; url?: string } = {};
      const platformTrimmed = link.platform.trim();
      const urlTrimmed = link.url.trim();

      // If either platform or url is provided, both are required and must be valid
      if (platformTrimmed || urlTrimmed) {
        if (!platformTrimmed) {
          err.platform = 'Platform name is required';
          hasError = true;
        } else if (!/\p{L}/u.test(platformTrimmed)) {
          err.platform = 'Platform must contain at least one letter';
          hasError = true;
        } else if (platformTrimmed.length > 50) {
          err.platform = 'Platform must be 50 characters or less';
          hasError = true;
        }

        if (!urlTrimmed) {
          err.url = 'URL is required';
          hasError = true;
        } else if (urlTrimmed.length > 255) {
          err.url = 'URL must be 255 characters or less';
          hasError = true;
        } else if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(urlTrimmed)) {
          err.url = 'Please enter a valid URL (e.g. https://instagram.com/user)';
          hasError = true;
        } else {
          const domainError = getPlatformDomainError(platformTrimmed, urlTrimmed);
          if (domainError) {
            err.url = domainError;
            hasError = true;
          }
        }
      }

      rowErrors.push(err);
    });

    return { hasError, rowErrors };
  };

  const handleSave = async () => {
    if (!currentUser) return;
    setHasSubmitted(true);

    const { hasError } = validateLinks();
    if (hasError) {
      toast.error('Please resolve validation errors before saving.');
      return;
    }

    // Filter out completely blank rows
    const validLinks = links
      .map((l) => ({ platform: l.platform.trim(), url: l.url.trim() }))
      .filter((l) => l.platform !== '' && l.url !== '');

    if (validLinks.length > 10) {
      toast.error('Maximum 10 social links allowed.');
      return;
    }

    try {
      const uid = currentUser.uid || '';
      await updateProfileBackend({
        uid,
        data: { socialLinks: validLinks },
      });
      setIsEditing(false);
      setActiveAutocompleteIndex(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setActiveAutocompleteIndex(null);
    setHasSubmitted(false);
    setLinks(currentUser?.socialLinks?.length ? currentUser.socialLinks : []);
  };

  const { rowErrors } = validateLinks();

  // View mode content
  const renderViewContent = () => {
    if (!links || links.length === 0) {
      return (
        <div className="social-links-empty">
          <p className="empty-text">{'No social media links added yet.'}</p>
        </div>
      );
    }

    return (
      <div className="social-links-grid">
        {links.map((link, idx) => (
          <a
            key={`${link.platform}-${idx}`}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link-card"
          >
            <div className="platform-icon-box">{getSocialIcon(link.platform)}</div>
            <div className="link-details">
              <span className="platform-label">{link.platform}</span>
              <span className="link-url-text">{link.url.replace(/^https?:\/\/(www\.)?/, '')}</span>
            </div>
            <div className="external-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </div>
          </a>
        ))}
      </div>
    );
  };

  // Edit mode content
  const renderEditContent = () => {
    return (
      <div className="social-links-edit-container" ref={dropdownRef}>
        <div className="links-edit-list">
          {links.map((link, index) => {
            const platformErr = hasSubmitted ? rowErrors[index]?.platform : undefined;
            const urlErr = hasSubmitted ? rowErrors[index]?.url : undefined;

            // Filter preset platforms based on typed input
            const filteredPresets = PRESET_PLATFORMS.filter((p) =>
              p.name.toLowerCase().includes(link.platform.trim().toLowerCase()),
            );

            return (
              <div key={index} className="social-link-row">
                {/* LINK TITLE field */}
                <div className="field-group link-title-field">
                  <label className="field-label">{'LINK TITLE'}</label>
                  <div className="input-with-icon">
                    <div className="prefix-icon">{getSocialIcon(link.platform)}</div>
                    <input
                      type="text"
                      className={`field-input ${platformErr ? 'error-border' : ''}`}
                      placeholder="e.g. My website, Instagram..."
                      value={link.platform}
                      maxLength={50}
                      onChange={(e) => {
                        handleUpdateField(index, 'platform', e.target.value);
                        setActiveAutocompleteIndex(index);
                      }}
                      onFocus={() => setActiveAutocompleteIndex(index)}
                      onBlur={(e) => handlePlatformBlur(index, e.target.value)}
                    />
                  </div>
                  {platformErr && <span className="inline-error-msg">{platformErr}</span>}

                  {/* Autocomplete Dropdown */}
                  {activeAutocompleteIndex === index && filteredPresets.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {filteredPresets.map((preset) => (
                        <button
                          type="button"
                          key={preset.name}
                          className="autocomplete-item"
                          onMouseDown={(e) => {
                            e.preventDefault(); // prevent blur before click registers
                            handleSelectAutocomplete(index, preset);
                          }}
                        >
                          <span className="item-icon">{getSocialIcon(preset.name)}</span>
                          <span className="item-name">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* URL field */}
                <div className="field-group url-field">
                  <label className="field-label">{'URL'}</label>
                  <div className="url-input-container">
                    <input
                      type="text"
                      className={`field-input ${urlErr ? 'error-border' : ''}`}
                      placeholder="https://"
                      value={link.url}
                      maxLength={255}
                      onChange={(e) => handleUpdateField(index, 'url', e.target.value)}
                      onBlur={(e) => handleUrlBlur(index, e.target.value)}
                    />
                    <button
                      type="button"
                      className="remove-link-row-btn"
                      onClick={() => handleRemoveLinkClick(index)}
                      aria-label="Remove link"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  {urlErr && <span className="inline-error-msg">{urlErr}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Dashed Add new link button matching screenshot */}
        <button
          type="button"
          className="add-new-link-btn"
          onClick={handleAddLink}
          disabled={links.length >= 10 || isPending}
        >
          <span className="plus-icon">{'+'}</span>
          <span>{'Add new link'}</span>
        </button>
      </div>
    );
  };

  return (
    <>
      <ProfileSection
        title="Social Media & Links"
        isEditing={isEditing}
        isSaving={isPending}
        onEditToggle={setIsEditing}
        onSave={handleSave}
        onCancel={handleCancel}
        editContent={renderEditContent()}
      >
        {renderViewContent()}
      </ProfileSection>

      {/* Delete confirmation modal */}
      <Modal
        open={linkToRemove !== null}
        footer={null}
        onCancel={() => setLinkToRemove(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <ProfileRemove2Icon />
          </div>
          <h2>{'Delete Social Link?'}</h2>
          <p>
            {'Are you sure you want to remove '}
            <strong>
              {linkToRemove !== null && links[linkToRemove]?.platform
                ? links[linkToRemove].platform
                : 'this social link'}
            </strong>
            {'?'}
          </p>
          <div className="logout-actions">
            <button className="btn-logout" onClick={confirmRemoveLink}>
              <ProfileRemoveIcon /> {'Delete'}
            </button>
            <button className="btn-cancel" onClick={() => setLinkToRemove(null)}>
              {'Cancel'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
